<script lang="ts">import { goto } from "$app/navigation";
import { isPromoExternalAbsoluteUrl, isRewardPromo, isRewardPromoClaimReady, openPromoExternalInNewTab } from "../../utils";
import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { loggedIn, ModalDialog, pushModal, refreshVipState, stateOpenModal, stateVipState, toSiteUrl } from "@infinitro/global";
import { get } from "svelte/store";
export let isDetail = false;
export let promo;
export let active = false;
const clickSignUp = () => {
	pushModal(ModalDialog.Auth, { tab: "signup" });
};
const cashier = () => {
	pushModal(ModalDialog.Cashier);
};
$: useLoggedInPromoCta = $loggedIn === true || ($stateUi.promotions?.promoCtasMatchLoggedInWhenLoggedOut ?? false);
const goToCasino = async () => {
	await goto(toSiteUrl(`/casino`, useLoggedInPromoCta));
};
const resolveUrl = () => {
	if (promo.conditionalUrl) {
		return $loggedIn === true ? promo.conditionalUrl.loggedInUrl : promo.conditionalUrl.loggedOutUrl;
	}
	return promo.url;
};
const getButtonLink = () => {
	if (promo.promotionUrl) {
		return isPromoExternalAbsoluteUrl(promo.promotionUrl) ? promo.promotionUrl : toSiteUrl(promo.promotionUrl, useLoggedInPromoCta);
	}
	const resolvedUrl = resolveUrl();
	const toSiteIsLoggedIn = promo.conditionalUrl ? $loggedIn === true : useLoggedInPromoCta;
	return isPromoExternalAbsoluteUrl(resolvedUrl) ? resolvedUrl : toSiteUrl(resolvedUrl, toSiteIsLoggedIn);
};
const navigateToPromoLink = async (url) => {
	if (isPromoExternalAbsoluteUrl(url)) {
		openPromoExternalInNewTab(url);
		return;
	}
	await goto(url);
};
const goToCustom = async () => {
	if (!promo.customAction) return;
	if (isPromoExternalAbsoluteUrl(promo.customAction)) {
		openPromoExternalInNewTab(promo.customAction);
		return;
	}
	await goto(toSiteUrl(promo.customAction, useLoggedInPromoCta));
};
const goToReferral = async () => {
	await goto(toSiteUrl(`${useLoggedInPromoCta ? "/referral-dashboard/" : "/referral/"}`, useLoggedInPromoCta));
};
const getRewardFallbackUrl = () => toSiteUrl($loggedIn === true ? "/vip-dashboard/" : "/vip/", $loggedIn === true);
const openRewardsDropdown = () => {
	stateOpenModal.set(ModalDialog.RewardsDropdown);
};
const handleRewardPromoAction = async () => {
	if ($loggedIn !== true) {
		await goto(getRewardFallbackUrl());
		return;
	}
	const vipStateSnapshot = get(stateVipState);
	if (Array.isArray(vipStateSnapshot?.rewards)) {
		if (isRewardPromoClaimReady(promo, vipStateSnapshot)) {
			openRewardsDropdown();
			return;
		}
		await goto(getRewardFallbackUrl());
		return;
	}
	await refreshVipState();
	const refreshedVipState = get(stateVipState);
	if (isRewardPromoClaimReady(promo, refreshedVipState)) {
		openRewardsDropdown();
		return;
	}
	await goto(getRewardFallbackUrl());
};
$: isExpired = $stateUi?.promotions?.showExpiredPromotions && !!promo?.endDate && Date.now() > promo?.endDate.getTime();
$: useAlternative2 = $stateUi.promotions.promotionsStyle === "alternative2";
$: defaultButtonText = useAlternative2 ? Verbiage.more_info : Verbiage.read_more;
$: promoButtonText = promo.buttonText || defaultButtonText;
$: rewardPromoClaimReady = isRewardPromo(promo) && isRewardPromoClaimReady(promo, $stateVipState);
$: rewardPromoButtonText = rewardPromoClaimReady ? Verbiage.claim_now : Verbiage.more_info;
$: resolvedPromoButtonText = isRewardPromo(promo) ? rewardPromoButtonText : promoButtonText;
const translatePromoText = (text) => $t(text);
$: actionButtonHref = getButtonLink();
$: actionButtonHrefExternal = isPromoExternalAbsoluteUrl(actionButtonHref);
</script>

<div class="button-container">
	{#if useLoggedInPromoCta}
		{#if promo.action}
			<Button
				text={isExpired
					? $t(Verbiage.expired)
					: !active
						? $t(Verbiage.coming_soon)
						: !isDetail
							? promo.id === 'deposit_bonus' || promo.id === 'welcome-deposit-bonus'
								? $t(Verbiage.deposit)
								: translatePromoText(resolvedPromoButtonText)
							: translatePromoText(resolvedPromoButtonText)}
				variant={!active || isExpired ? 'tertiary' : 'primary'}
				width={'120px'}
				onClick={(e) => {
					if (!active || isExpired) return;
					e.stopPropagation();
					if (isRewardPromo(promo)) {
						handleRewardPromoAction();
					} else if (promo.id === 'deposit_bonus' || promo.id === 'welcome-deposit-bonus') {
						cashier();
					} else if (promo.id === 'referral') {
						goToReferral();
					} else if (promo.customAction) {
						goToCustom();
					} else if (promo.useUrlForDetail || promo.promotionUrl || promo.conditionalUrl) {
						navigateToPromoLink(getButtonLink());
					} else {
						goToCasino();
					}
				}}
				disabledWithoutStyles={!active || isExpired}
				whiteSpace="nowrap"
			/>
		{:else}
			<a
				href={actionButtonHref}
				target={actionButtonHrefExternal ? '_blank' : undefined}
				rel={actionButtonHrefExternal ? 'noopener noreferrer' : undefined}
				on:click={(e) => {
					if (!active || isExpired) e.preventDefault();
				}}
			>
				<Button
					text={isExpired
						? $t(Verbiage.expired)
						: !active
							? $t(Verbiage.coming_soon)
							: !isDetail
								? $t(defaultButtonText)
								: translatePromoText(resolvedPromoButtonText)}
					variant={!active || isExpired ? 'tertiary' : 'primary'}
					width={'120px'}
					disabledWithoutStyles={!active || isExpired}
					whiteSpace="nowrap"
				/>
			</a>
		{/if}
	{:else}
		<Button
			text={isExpired
				? $t(Verbiage.expired)
				: !active
					? $t(Verbiage.coming_soon)
					: isRewardPromo(promo)
						? translatePromoText(rewardPromoButtonText)
						: !isDetail
							? promo.id === 'referral'
								? translatePromoText(promoButtonText)
								: $t(defaultButtonText)
							: $t(Verbiage.sign_up)}
			variant={!active || isExpired ? 'tertiary' : 'primary'}
			width={'120px'}
			whiteSpace="nowrap"
			onClick={!active || isExpired
				? () => null
				: isRewardPromo(promo)
					? handleRewardPromoAction
					: clickSignUp}
			disabledWithoutStyles={!active || isExpired}
		/>
	{/if}
</div>

<style>
	.button-container {
    display: flex;
    height: 100%;
    min-width: 125px;
    align-items: center;
    justify-content: flex-end
}
</style>
